import { ProcurementAxios } from "api/ProcurementAxios";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import FileUploadInput from "Common/Components/FileUploadInput/FileUploadInput";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { useFormik } from "formik";
import { showCustomSwal } from "helpers/swalHelper";
import React, { Fragment, useEffect, useState } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  addPurchaseRequest,
  fetchPurchaseRequestId,
  updatePurchaseRequest,
} from "slices/Procurement/purchase-request/thunk";
import * as Yup from "yup";
import ProductFormTable from "./Components/ProductFormTable";
import AddTagsForPurchaseRequest from "../../Components/AddTagsForPurchaseRequest";
import LoadingSpinner from "components/LoadingSpinner";
import NewFileUploadInput from "components/FilePreview/FileInputPreview";
import FileUpload from "Common/Components/FileUpload.tsx/FileUpload";
import { ChevronDown, ChevronUp } from "lucide-react";
import { MecordColors } from "Constants/Color";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";

interface ProductDetail {
  product_id: string;
  product_code: string;
  product_details: {
    product_name: string;
    product_sku: string;
    category: string;
    sub_category: string;
    make: string;
    model: string;
    unit_name: string;
    unit_price: number;
    gst_percentage: number;
  };
  quantity: number;
  description: string;
}

interface FormValues {
  pr_date: string;
  initiated_by: string;
  notes: string;
  document: File | null;
  products: ProductDetail[];
  attachments: (File | string)[];
  tags: string[];
}

const PurchaseRequestEdit: React.FC = () => {
  const { pr_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const { state } = useLocation();
  const [isNotesOpen, setIsNotesOpen] = useState(true);
  const [existingDocuments, setExistingDocuments] = useState<any>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      pr_date: new Date().toISOString().split("T")[0],
      document: null,
      initiated_by: "",
      notes: "",
      products: [],
      attachments: [],
      tags: [],
    },
    validationSchema: Yup.object({
      pr_date: Yup.string().required("Please select Requested Date"),
      initiated_by: Yup.string().trim().required("Please enter Initiated By"),
      notes: Yup.string(),
      products: Yup.array().min(1, "At least one product is required"),
    }),
    onSubmit: async (values) => {
      if (!values.products || values.products.length === 0) {
        showCustomSwal({
          icon: "error",
          text: "At least one product is required",
        });
        return;
      }

      const payload = {
        pr_date: new Date(values?.pr_date)?.toISOString(),
        initiated_by: values?.initiated_by || "",
        notes: values?.notes || "",
        products:
          values?.products?.map((p: any) => ({
            product_id:
              p?.product_id ||
              p?.product?.product_id ||
              p?.product_details?.product_id,
            product_code: p?.product_code ?? "",
            product_details: p?.product_details ?? { ...p?.product },
            quantity: Number(p?.quantity),
            unit_price: Number(p?.product?.unit_price ?? p?.unit_price ?? 0),
            description: p?.description ?? "",
          })) || [],
      };

      const documentFile = formik.values.document as File | null;
      setIsSubmitting(true);

      const formData = new FormData();
      formData.append("pr_date", payload.pr_date);
      formData.append("initiated_by", payload.initiated_by);
      formData.append("notes", payload.notes ?? "");
      formData.append("tags", JSON.stringify(values.tags ?? []));
      formData.append("products", JSON.stringify(payload.products || []));
      // formData.append("data", JSON.stringify(payload));

      // if (documentFile) {
      //   if (typeof documentFile === "string") {
      //     formData.append("pr_document", documentFile);
      //   } else {
      //     formData.append("pr_document", documentFile, documentFile.name);
      //   }
      // }

      if (values.attachments?.length) {
        values.attachments.forEach((item: any) => {
          const file =
            item instanceof File ? item : item.file || item.originFileObj;

          if (file instanceof File) {
            formData.append("attachments", file); // ✅ NOT attachments[]
          }
        });
      }

      if (existingDocuments.length > 0) {
        formData.append(
          "existingDocuments",
          JSON.stringify(existingDocuments ?? []),
        );
      }

      try {
        if (pr_id) {
          await dispatch(updatePurchaseRequest({ formData, pr_id })).unwrap();
          toast.success("Purchase Request Updated Successfully");
          navigate("/purchase-management/purchase-request");
        }
      } catch (e) {
        handleSubmissionError(e);
        setPageLoading(false);
      } finally {
        setIsSubmitting(false);
      }

      function handleSubmissionError(err: any) {
        // Handle errors from rejectWithValue (thunk errors)
        // When using unwrap(), the error is the payload directly from rejectWithValue
        // The thunk returns: err.response?.data || "Failed to add PR"

        // If error is a string (from rejectWithValue fallback)
        if (typeof err === "string") {
          toast.error(err);
          return;
        }

        // If error is an object (from err.response?.data)
        if (err && typeof err === "object") {
          const backendMessage = err?.message;
          const errType = err?.errors || {};

          if (errType && Object.keys(errType).length > 0) {
            Object.keys(errType).forEach((key: string) => {
              // if errors are nested arrays/objects, show first message
              const msg = Array.isArray((errType as any)[key])
                ? (errType as any)[key][0]
                : JSON.stringify((errType as any)[key]);
              showCustomSwal({ icon: "error", text: `${msg}` });
            });
            return;
          }

          if (backendMessage) {
            toast.error(backendMessage);
            return;
          }
        }

        // Handle direct axios errors (fallback - should not happen with unwrap)
        const status = err?.response?.status;
        const axiosMessage = err?.response?.data?.message;
        const axiosErrType = err?.response?.data?.errors || {};

        if (axiosErrType && Object.keys(axiosErrType).length > 0) {
          Object.keys(axiosErrType).forEach((key: string) => {
            const msg = Array.isArray((axiosErrType as any)[key])
              ? (axiosErrType as any)[key][0]
              : JSON.stringify((axiosErrType as any)[key]);
            showCustomSwal({ icon: "error", text: `${msg}` });
          });
          return;
        }

        if (status === 404 && axiosMessage) {
          toast.error(axiosMessage);
        } else {
          toast.error("Something went wrong. Please try again.");
        }
      }
    },
  });

  useScrollToFormikError(formik.errors, formik.submitCount, formik.isSubmitting);

  useEffect(() => {
    if (pr_id) {
      setPageLoading(true);
      dispatch(fetchPurchaseRequestId(pr_id))
        .unwrap()
        .then((res: any) => {
          const data = res.data;
          const loadedProducts = data.products || [];

          formik.setValues({
            pr_date: data?.pr_date?.split("T")[0],
            initiated_by: data.initiated_by || "",
            notes: data.notes || "",
            document: data?.pr_document,
            products: loadedProducts,
            attachments: [],
            tags: Array.isArray(data?.tags) ? data.tags : [],
          });

          setExistingDocuments(data?.attachments || []);
        })
        .catch((e: any) => {
          function handleSubmissionError(err: any) {
            if (typeof err === "string") {
              toast.error(err);
              return;
            }

            // If error is an object (from err.response?.data)
            if (err && typeof err === "object") {
              const backendMessage = err?.message;
              const errType = err?.errors || {};

              if (errType && Object.keys(errType).length > 0) {
                Object.keys(errType).forEach((key: string) => {
                  // if errors are nested arrays/objects, show first message
                  const msg = Array.isArray((errType as any)[key])
                    ? (errType as any)[key][0]
                    : JSON.stringify((errType as any)[key]);
                  showCustomSwal({ icon: "error", text: `${msg}` });
                });
                return;
              }

              if (backendMessage) {
                toast.error(backendMessage);
                return;
              }
            }

            // Handle direct axios errors (fallback - should not happen with unwrap)
            const status = err?.response?.status;
            const axiosMessage = err?.response?.data?.message;
            const axiosErrType = err?.response?.data?.errors || {};

            if (axiosErrType && Object.keys(axiosErrType).length > 0) {
              Object.keys(axiosErrType).forEach((key: string) => {
                const msg = Array.isArray((axiosErrType as any)[key])
                  ? (axiosErrType as any)[key][0]
                  : JSON.stringify((axiosErrType as any)[key]);
                showCustomSwal({ icon: "error", text: `${msg}` });
              });
              return;
            }

            if (status === 404 && axiosMessage) {
              toast.error(axiosMessage);
            } else {
              toast.error("Something went wrong. Please try again.");
            }
          }
          handleSubmissionError(e);
          setPageLoading(false);
        })
        .finally(() => {
          setPageLoading(false);
        });
    }
  }, []);

  return (
    <Fragment>
      <PageContainer>
        <PageBreadcrumb
          title={pr_id ? "Edit Purchase Request" : "Create Purchase Request"}
          titleLink="/purchase-management/purchase-request"
          breadcrumbs={[
            {
              label: "Purchase Request",
              to: "/purchase-management/purchase-request",
            },
            {
              label: pr_id
                ? "Edit Purchase Request"
                : "Create Purchase Request",
              to: "",
            },
          ]}
          backNavigationPath={-1}
        />
        <hr />
        {pageLoading ? (
          <LoadingSpinner />
        ) : (
          <Form
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              formik.handleSubmit();
            }}
          >
            <Row className="mb-4"> 
              <Col lg={4} className="mb-0">
                <Form.Label>
                  PR Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  id="pr_date"
                  name="pr_date"
                  value={formik.values.pr_date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={!!formik.errors.pr_date && formik.touched.pr_date}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.pr_date}
                </Form.Control.Feedback>
              </Col>

              <Col lg={4} className="mb-0">
                <Form.Label>
                  Initiated By <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="string"
                  id="initiated_by"
                  name="initiated_by"
                  placeholder="Enter Name"
                  value={formik.values.initiated_by}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[a-zA-Z\s]*$/.test(value)) {
                      formik.setFieldValue("initiated_by", value);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.initiated_by && formik.touched.initiated_by
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.initiated_by}
                </Form.Control.Feedback>
              </Col>
              <Col lg={4} className="mb-0">
                <AddTagsForPurchaseRequest
                  label="Tags"
                  placeholder="Enter tags"
                  value={formik.values.tags}
                  onChange={(tags) => formik.setFieldValue("tags", tags)}
                />
              </Col>
              {/* <Col lg={4} className="mb-0">
                <NewFileUploadInput
                  label="Attach File"
                  name="document"
                  formik={formik}
                />
              </Col>

              <Col lg={6} className="mb-4">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  type="text"
                  name="notes"
                  placeholder="Enter Notes"
                  value={formik.values.notes}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={!!formik.errors.notes && formik.touched.notes}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.notes}
                </Form.Control.Feedback>
              </Col> */}
            </Row>
            <div className="mb-4" id="products" >
              <h6 className="fw-semibold mb-2" style={{ fontSize: "0.9rem" }}>
                Product
              </h6>
              <ProductFormTable mainFormik={formik} />
              {formik.errors.products &&
                formik.touched.products &&
                typeof formik.errors.products === "string" && (
                  <div
                    className="text-danger"
                    style={{ fontSize: "0.875rem", marginTop: "0.25rem" }}
                  >
                    {formik.errors.products}
                  </div>
                )}
            </div>

            {/* Notes & Attachment Section */}
            <Row className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1 gap-2">
                <h5 className="fw-semibold">Notes & Attachments</h5>
              </div>
              <Col lg={6} md={12}>
                <div
                  className="rounded mb-3"
                  style={{ backgroundColor: MecordColors?.LightGrayColor }}
                >
                  {/* Notes Header - Collapsible */}
                  <div
                    className="d-flex justify-content-start align-items-center p-2 rounded"
                    style={{
                      cursor:
                        !formik.values.notes ||
                        formik.values.notes.trim() === ""
                          ? "pointer"
                          : "default",
                      backgroundColor: MecordColors?.LightGrayColor,
                    }}
                    onClick={() => {
                      // Only allow closing if notes are empty
                      if (
                        !formik.values.notes ||
                        formik.values.notes.trim() === ""
                      ) {
                        setIsNotesOpen(!isNotesOpen);
                      }
                    }}
                  >
                    {(!formik.values.notes ||
                      formik.values.notes.trim() === "") && (
                      <div className="me-1">
                        {isNotesOpen ? (
                          <ChevronUp size={20} />
                        ) : (
                          <ChevronDown size={20} />
                        )}
                      </div>
                    )}

                    <h6
                      className="fw-semibold mb-0"
                      style={{ fontSize: "0.9rem" }}
                    >
                      Notes
                    </h6>
                  </div>

                  {/* Notes Content - Collapsible */}
                  {isNotesOpen && (
                    <div className="p-0">
                      <Form.Group>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          name="notes"
                          placeholder="Enter your notes"
                          value={formik.values.notes || ""}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          style={{ maxHeight: "400px", resize: "vertical" }}
                          isInvalid={
                            formik.touched.notes && !!formik.errors.notes
                          }
                        />

                        <Form.Control.Feedback type="invalid">
                          {formik.touched.notes && formik.errors.notes}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </div>
                  )}
                </div>
              </Col>
              <Col lg={6} md={12}>
                <div>
                  {/* <h6 className="fw-semibold mb-2">Attach Files</h6> */}
                  <Form.Group>
                    <FileUpload
                      label=""
                      multiple={true}
                      value={formik.values.attachments} // 👈 NEW FILES ONLY
                      existingFiles={existingDocuments} // 👈 EXISTING DOCS
                      onChange={(files) =>
                        formik.setFieldValue("attachments", files)
                      }
                      onRemoveExisting={(index) => {
                        const updatedDocs = [...existingDocuments];
                        updatedDocs.splice(index, 1);
                        setExistingDocuments(updatedDocs);

                        // Optional: track deleted docs for backend
                        // setDeletedDocuments([...deletedDocuments, existingDocuments[index]]);
                      }}
                    />
                  </Form.Group>
                </div>
              </Col>
            </Row>

            <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
              <CancelButton
                onClick={() =>
                  navigate("/purchase-management/purchase-request")
                }
                disabled={isSubmitting}
              />
              <PrimarySubmitButton
                label="Submit"
                isLoading={isSubmitting}
                loadingLabel="Submitting..."
              />
            </div>
          </Form>
        )}
      </PageContainer>
    </Fragment>
  );
};

export default PurchaseRequestEdit;
